import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { useNotifications } from "../../context/NotificationContext";
import "./NotificationDropdown.css";

const parseData = (value) => {
  if (!value) return {};
  if (typeof value === "object") return value;
  try {
    const parsed = JSON.parse(value);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
};

const getNotificationTarget = (notification, isAdmin, currentOrigin) => {
  const data = parseData(notification.Data);
  const type = String(notification.Type || "").toLowerCase();
  const isStoreNotification = type.includes("store") || type.includes("request");
  const isAccountNotification = type.includes("account");
  const communicationPath = isAdmin ? "/admin/communication" : "/communication";
  const storeMessageTarget =
    type === "store_request_message" && data.requestId
      ? `${communicationPath}?requestId=${encodeURIComponent(data.requestId)}`
      : null;
  const fallback = isStoreNotification
    ? isAdmin
      ? "/admin/requests"
      : "/request-store"
    : isAccountNotification && isAdmin
      ? "/admin/clients"
      : communicationPath;

  try {
    const target = new URL(storeMessageTarget || data.link || fallback, currentOrigin);
    if (!["http:", "https:"].includes(target.protocol)) return fallback;
    let path = target.pathname;
    if (["/support", "/notifications"].includes(path)) {
      path = isAdmin ? "/admin/communication" : "/communication";
    } else if (["/admin/support", "/admin/notifications"].includes(path)) {
      path = "/admin/communication";
    }
    if (isAdmin && !path.startsWith("/admin")) return fallback;
    if (!isAdmin && path.startsWith("/admin")) return fallback;
    return `${path}${target.search}${target.hash}`;
  } catch {
    return fallback;
  }
};

const getNotificationHeading = (notification) => {
  const type = String(notification.Type || "").toLowerCase();
  if (type === "store_request_message") return "Communication Store Update";
  if (type.startsWith("support_")) {
    return `Communication · ${notification.Title}`;
  }
  return notification.Title;
};

const NotificationDropdown = ({ admin = false }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const {
    notifications,
    unreadCount,
    markAsRead,
    markAllAsRead,
    refreshNotifications,
  } = useNotifications();
  const [isOpen, setIsOpen] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const containerRef = useRef(null);
  const isAdmin = admin && user?.Role === "admin";

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    const handleEscape = (event) => {
      if (event.key === "Escape") setIsOpen(false);
    };
    document.addEventListener("mousedown", handleOutsideClick);
    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
      document.removeEventListener("keydown", handleEscape);
    };
  }, []);

  useEffect(() => {
    setIsOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (isOpen) refreshNotifications();
  }, [isOpen, refreshNotifications]);

  const handleOpen = async (notification) => {
    setError("");
    try {
      if (!notification.IsRead) {
        await markAsRead(notification.NotificationID);
      }
      setIsOpen(false);
      navigate(
        getNotificationTarget(notification, isAdmin, window.location.origin),
      );
    } catch (requestError) {
      setError(
        requestError.response?.data?.error ||
          "Could not update this notification.",
      );
    }
  };

  const handleMarkOne = async (event, notification) => {
    event.stopPropagation();
    setError("");
    try {
      await markAsRead(notification.NotificationID);
    } catch (requestError) {
      setError(
        requestError.response?.data?.error ||
          "Could not update this notification.",
      );
    }
  };

  const handleMarkAll = async () => {
    setBusy(true);
    setError("");
    try {
      await markAllAsRead();
    } catch (requestError) {
      setError(
        requestError.response?.data?.error ||
          "Could not update notifications.",
      );
      await refreshNotifications();
    } finally {
      setBusy(false);
    }
  };

  const recentNotifications = notifications.slice(0, 6);

  return (
    <div className="notification-dropdown" ref={containerRef}>
      <button
        className={admin ? "admin-notification" : "notification-bell"}
        type="button"
        onClick={() => setIsOpen((open) => !open)}
        aria-label="Notifications"
        aria-expanded={isOpen}
        aria-haspopup="dialog"
      >
        <i className="fas fa-bell" />
        {unreadCount > 0 && (
          <span
            className={admin ? "admin-notification-badge" : "notification-badge"}
          >
            {unreadCount > 99 ? "99+" : unreadCount}
          </span>
        )}
      </button>

      {isOpen && (
        <section
          className="notification-dropdown-panel"
          role="dialog"
          aria-label="Recent notifications"
        >
          <header className="notification-dropdown-header">
            <div className="notification-dropdown-title">
              <strong>Notifications</strong>
              <span>{unreadCount ? `${unreadCount} unread` : "All caught up"}</span>
            </div>
            <div className="notification-dropdown-header-actions">
              <button
                className="notification-dropdown-mark-all"
                type="button"
                onClick={handleMarkAll}
                disabled={!unreadCount || busy}
              >
                {busy ? "Saving..." : "Mark all read"}
              </button>
              <button
                className="notification-dropdown-close"
                type="button"
                onClick={() => setIsOpen(false)}
                aria-label="Close notifications"
                title="Close notifications"
              >
                <i className="fas fa-times" aria-hidden="true" />
              </button>
            </div>
          </header>
          {error && <p className="notification-dropdown-error">{error}</p>}
          <div className="notification-dropdown-list">
            {recentNotifications.length ? (
              recentNotifications.map((notification) => (
                <div
                  className={`notification-dropdown-row ${notification.IsRead ? "is-read" : "is-unread"}`}
                  key={notification.NotificationID}
                >
                  <button
                    type="button"
                    className="notification-dropdown-open"
                    onClick={() => handleOpen(notification)}
                  >
                    <strong>{getNotificationHeading(notification)}</strong>
                    <span>{notification.Message}</span>
                    <small>
                      {notification.CreatedAt
                        ? new Date(notification.CreatedAt).toLocaleString()
                        : ""}
                    </small>
                  </button>
                  {!notification.IsRead && (
                    <button
                      type="button"
                      className="notification-dropdown-mark"
                      onClick={(event) => handleMarkOne(event, notification)}
                      aria-label={`Mark ${notification.Title} as read`}
                      title="Mark as read"
                    >
                      <i className="fas fa-check" />
                    </button>
                  )}
                </div>
              ))
            ) : (
              <p className="notification-dropdown-empty">No notifications yet.</p>
            )}
          </div>
        </section>
      )}
    </div>
  );
};

export default NotificationDropdown;
